import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  Briefcase, 
  MapPin, 
  DollarSign, 
  Calendar, 
  Clock,
  Users,
  Plus,
  Filter,
  Search,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { applyPageMetadata } from '../utils/seo';

function JobsPage() {
  const { user, isBarOwner, isBartender } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobForm, setShowJobForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    job_type: 'full_time',
    hourly_rate: '',
    start_date: '',
    end_date: '',
    required_experience: 0,
    required_skills: [],
    location: {
      city: '',
      state: '',
      address: ''
    }
  });

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/jobs?limit=50');
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    const title = isBarOwner ? 'Owner Jobs | BarsConnect' : 'Browse Jobs | BarsConnect';
    const description = isBarOwner
      ? 'Create and manage your bar job listings on BarsConnect.'
      : 'Browse bartender opportunities and apply quickly on BarsConnect.';

    const cleanup = applyPageMetadata({
      title,
      description,
      robots: 'noindex,nofollow',
      ogTitle: title,
      ogDescription: description,
      twitterTitle: title,
      twitterDescription: description,
    });

    return cleanup;
  }, [isBarOwner]);

  const handleJobSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post('/jobs', {
        ...jobForm,
        hourly_rate: parseFloat(jobForm.hourly_rate),
        required_experience: parseInt(jobForm.required_experience)
      });
      
      toast.success('Job posted successfully!');
      setShowJobForm(false);
      setJobForm({
        title: '',
        description: '',
        job_type: 'full_time',
        hourly_rate: '',
        start_date: '',
        end_date: '',
        required_experience: 0,
        required_skills: [],
        location: {
          city: '',
          state: '',
          address: ''
        }
      });
      fetchJobs();
    } catch (error) {
      console.error('Failed to post job:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to post job';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyToJob = async (jobId) => {
    try {
      await axios.post(`/jobs/${jobId}/apply`, {
        cover_message: 'I am interested in this position and believe my skills are a great match.'
      });
      toast.success('Application submitted successfully!');
      fetchJobs(); // Refresh to update application counts
    } catch (error) {
      console.error('Failed to apply to job:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to apply to job');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = jobTypeFilter === 'all' || job.job_type === jobTypeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="jobs-page-loading-state">
        <div className="space-y-3 mb-8">
          <div className="h-10 w-64 rounded-md bg-primary/10 animate-pulse" />
          <div className="h-5 w-80 max-w-full rounded-md bg-white/10 animate-pulse" />
        </div>
        <div className="h-20 rounded-xl border border-white/10 bg-white/5 animate-pulse mb-6" />
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="h-40 rounded-xl border border-white/10 bg-white/5 animate-pulse" data-testid={`jobs-page-loading-card-${index + 1}`} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="jobs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8" data-testid="jobs-page-header">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-2 font-['Barlow_Condensed']">
            {isBarOwner ? 'Job Management' : 'Find Jobs'}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground" data-testid="jobs-page-subtitle">
            {isBarOwner ? 'Post and manage your job listings' : 'Discover bartending opportunities near you'}
          </p>
        </div>
        
        {isBarOwner && (
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto" data-testid="jobs-page-owner-header-ctas">
            <Button asChild variant="outline" className="w-full sm:w-auto" data-testid="owner-jobs-instant-shift-btn">
              <Link to="/dashboard">Instant Shift Fill</Link>
            </Button>

            <Dialog open={showJobForm} onOpenChange={setShowJobForm}>
              <DialogTrigger asChild>
                <Button 
                  className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] shadow-[0_0_20px_-5px_rgba(255,45,138,0.5)]"
                  data-testid="post-job-btn"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Post Job
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto z-50" onPointerDownOutside={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed'] uppercase">Post New Job</DialogTitle>
                <DialogDescription>
                  Create a job listing to attract skilled bartenders
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleJobSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Job Title</Label>
                    <Input
                      id="title"
                      placeholder="Senior Bartender"
                      value={jobForm.title}
                      onChange={(e) => setJobForm({...jobForm, title: e.target.value})}
                      required
                      data-testid="job-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="job_type">Job Type</Label>
                    <Select value={jobForm.job_type} onValueChange={(value) => setJobForm({...jobForm, job_type: value})}>
                      <SelectTrigger data-testid="job-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time">Full Time</SelectItem>
                        <SelectItem value="part_time">Part Time</SelectItem>
                        <SelectItem value="seasonal">Seasonal</SelectItem>
                        <SelectItem value="event">Event</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Job Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe the role, responsibilities, and what you're looking for..."
                    value={jobForm.description}
                    onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
                    rows={4}
                    required
                    data-testid="job-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
                    <Input
                      id="hourly_rate"
                      type="number"
                      min="10"
                      max="100"
                      step="0.50"
                      placeholder="25.00"
                      value={jobForm.hourly_rate}
                      onChange={(e) => setJobForm({...jobForm, hourly_rate: e.target.value})}
                      required
                      data-testid="hourly-rate-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={jobForm.start_date}
                      onChange={(e) => setJobForm({...jobForm, start_date: e.target.value})}
                      required
                      data-testid="start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="required_experience">Required Experience (years)</Label>
                    <Input
                      id="required_experience"
                      type="number"
                      min="0"
                      max="20"
                      placeholder="2"
                      value={jobForm.required_experience}
                      onChange={(e) => setJobForm({...jobForm, required_experience: e.target.value})}
                      data-testid="experience-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      placeholder="San Francisco"
                      value={jobForm.location.city}
                      onChange={(e) => setJobForm({...jobForm, location: {...jobForm.location, city: e.target.value}})}
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      placeholder="CA"
                      value={jobForm.location.state}
                      onChange={(e) => setJobForm({...jobForm, location: {...jobForm.location, state: e.target.value}})}
                      data-testid="state-input"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowJobForm(false)}
                    data-testid="cancel-job-btn"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))]"
                    data-testid="submit-job-btn"
                  >
                    {submitting ? 'Posting...' : 'Post Job'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Search and Filters */}
      <Card className="bg-white/5 backdrop-blur-md border border-white/5 mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search jobs by title or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="job-search-input"
                />
              </div>
            </div>
            <div className="md:w-48">
              <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
                <SelectTrigger data-testid="job-type-filter">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="full_time">Full Time</SelectItem>
                  <SelectItem value="part_time">Part Time</SelectItem>
                  <SelectItem value="seasonal">Seasonal</SelectItem>
                  <SelectItem value="event">Event</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4" data-testid="jobs-results-summary-row">
        <p className="text-sm text-muted-foreground" data-testid="jobs-results-count-text">
          Showing {filteredJobs.length} {filteredJobs.length === 1 ? 'job' : 'jobs'}
        </p>

        {(searchTerm || jobTypeFilter !== 'all') && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSearchTerm('');
              setJobTypeFilter('all');
            }}
            data-testid="jobs-clear-filters-btn"
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Jobs List */}
      {filteredJobs.length === 0 ? (
        <Card className="bg-white/5 backdrop-blur-md border border-white/5">
          <CardContent className="p-12 text-center">
            <Briefcase className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No jobs found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || jobTypeFilter !== 'all' ? 'Try adjusting your search criteria' : 'No job listings available at the moment'}
            </p>
            {isBarOwner && (
              <Button onClick={() => setShowJobForm(true)} data-testid="no-jobs-post-btn">
                Post Your First Job
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredJobs.map((job) => (
            <Card key={job.id} className="group bg-white/5 backdrop-blur-md border border-white/5 hover:border-white/20 transition-all duration-300" data-testid={`job-card-${job.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-white">{job.title}</h3>
                      <Badge 
                        variant={job.job_type === 'full_time' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {job.job_type.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        {job.location?.city ? `${job.location.city}, ${job.location.state}` : 'Location TBD'}
                      </div>
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 mr-1" />
                        {formatCurrency(job.hourly_rate)}/hr
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        Starts {formatDate(job.start_date)}
                      </div>
                      {job.required_experience > 0 && (
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {job.required_experience}+ years exp
                        </div>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed" data-testid={`job-description-${job.id}`}>
                      {job.description}
                    </p>

                    {job.bar_profile_snippet && (
                      <div className="mt-4 rounded-lg border border-white/10 p-3" data-testid={`job-owner-snippet-${job.id}`}>
                        <div className="flex items-start gap-3">
                          {job.bar_profile_snippet.gallery_image ? (
                            <img
                              src={job.bar_profile_snippet.gallery_image}
                              alt={`${job.bar_profile_snippet.business_name || 'Bar'} showcase`}
                              className="w-16 h-16 rounded-md object-cover border border-white/10"
                              data-testid={`job-owner-snippet-image-${job.id}`}
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-md border border-dashed border-white/10 flex items-center justify-center text-xs text-muted-foreground" data-testid={`job-owner-snippet-image-empty-${job.id}`}>
                              No Photo
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-white" data-testid={`job-owner-snippet-business-name-${job.id}`}>
                              {job.bar_profile_snippet.business_name || job.bar_owner?.name || 'Bar Owner'}
                            </p>
                            {job.bar_profile_snippet.verification_status === 'approved' && (
                              <Badge className="mt-1 bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-testid={`job-owner-snippet-verified-badge-${job.id}`}>
                                <ShieldCheck className="h-3 w-3 mr-1" />
                                Verified
                              </Badge>
                            )}
                            {job.bar_profile_snippet.establishment_type && (
                              <p className="text-xs text-muted-foreground" data-testid={`job-owner-snippet-establishment-type-${job.id}`}>
                                {job.bar_profile_snippet.establishment_type}
                              </p>
                            )}
                            {job.bar_profile_snippet.about_preview && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2" data-testid={`job-owner-snippet-about-${job.id}`}>
                                {job.bar_profile_snippet.about_preview}
                              </p>
                            )}
                            {(job.bar_profile_snippet.vibe_tags || []).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2" data-testid={`job-owner-snippet-tags-${job.id}`}>
                                {job.bar_profile_snippet.vibe_tags.slice(0, 4).map((tag) => (
                                  <Badge key={tag} variant="outline" className="text-[10px] px-2 py-0.5">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row lg:flex-col sm:items-center lg:items-end gap-2 lg:ml-6" data-testid={`job-card-cta-group-${job.id}`}>
                    {isBartender && (
                      <>
                        <Button 
                          onClick={() => handleApplyToJob(job.id)}
                          className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))]"
                          data-testid={`apply-btn-${job.id}`}
                        >
                          Apply Now
                        </Button>
                        <Button
                          asChild
                          variant="outline"
                          className="w-full sm:w-auto"
                          data-testid={`view-bar-profile-btn-${job.id}`}
                        >
                          <Link to={`/bars/${job.bar_owner_id}`}>View Bar Profile</Link>
                        </Button>
                      </>
                    )}
                    {job.applications_count > 0 && (
                      <div className="flex items-center text-sm text-muted-foreground" data-testid={`job-applications-count-${job.id}`}>
                        <Users className="h-4 w-4 mr-1" />
                        {job.applications_count} applications
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default JobsPage;
